import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { db } from '../../firebaseConfig';
import { doc, setDoc, increment } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import moment from 'moment';

const AnalyticsTracker = () => {
    const location = useLocation();
    const { userData } = useAuth();
    
    // Referencias para evitar re-renders y pérdida de timers
    const startTimeRef = useRef(Date.now());
    const currentPathRef = useRef(location.pathname);

    useEffect(() => {
        // Enviar datos al cerrar la pestaña o el navegador
        const handleBeforeUnload = () => flushAnalytics();

        // Enviar datos si la app web/PWA se va a segundo plano (Minimizar / Pantalla bloqueada en móviles)
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                flushAnalytics();
            } else if (document.visibilityState === 'visible') {
                startTimeRef.current = Date.now(); // Resetear el cronómetro al regresar a la app
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);  

    const flushAnalytics = () => {
        const durationSecs = Math.round((Date.now() - startTimeRef.current) / 1000);
        const path = currentPathRef.current;
        // Si no hay condominioId (ej. masteruser), agrupar su tráfico como GLOBAL
        const condoId = userData?.condominioId || 'GLOBAL';
        
        // Bajar umbral a >= 1 segundo para no descartar pruebas (aunque superuser ahora se ignorará)
        if (userData && durationSecs >= 0) { 
            let rolReal = userData.rol || 'desconocido';
            
            // 1. Ignorar personal maestro por completo
            if (rolReal === 'superuser' || rolReal === 'masteruser') return;

            // 2. Formatear la URL base
            const pathParts = path.split('/').filter(Boolean);
            const pathGroup = pathParts.length > 0 ? pathParts[0] : 'home';
            
            // 3. Determinar Proveedores
            if (rolReal === 'proveedor') {
                rolReal = 'proveedor_externo';
            } else if (rolReal === 'residente' && userData.proveedorId) {
                // Si el residente está usando un módulo de venta/proveeduría
                if (pathGroup === 'proveedor' || pathGroup === 'marketplace') {
                    rolReal = 'proveedor_interno';
                } else {
                    rolReal = 'residente'; // Módulos de vecino normales
                }
            }

            const mes = moment().format('YYYY-MM');
            const docRef = doc(db, 'analiticas_uso', `${condoId}_${mes}_${rolReal}_${pathGroup}`);
            
            // Inyectar atómicamente a Firestore sin bloquear el UI
            setDoc(docRef, {
                condominioId: condoId,
                mes: mes,
                pantalla: pathGroup,
                rol: rolReal,
                visitas: increment(1),
                tiempoTotalSegundos: increment(durationSecs)
            }, { merge: true })
            .then(() => console.log(`📊 Analytics Tracker: +1 visita a /${pathGroup} (${durationSecs}s) para ${condoId} [${rolReal}]`))
            .catch(err => console.error("Error logging analytics", err));
        }
    };

    useEffect(() => {
        // Cuando cambia la localización en el Router...
        if (currentPathRef.current !== location.pathname) {
            flushAnalytics();
            // Resetear cronómetro y puntero para la nueva ruta
            currentPathRef.current = location.pathname;
            startTimeRef.current = Date.now();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]); // No depende de userData para no disparar duplicados al iniciar sesión

    return null; // Componente invisible
};

export default AnalyticsTracker;
